"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Container principal da tabela.
 * MUDANÇA: Adicionei 'flex flex-col' no wrapper e 'flex-1' no container do scroll.
 * Isso garante que, se você definir uma altura no containerClassName, a tabela obedece.
 */
function Table({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<"table"> & { containerClassName?: string }) {
  return (
    <div
      className={cn(
        // Layout base
        "relative flex w-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm",
        // Adicione um max-h padrão aqui se quiser que TODAS as tabelas tenham limite,
        // ou controle via containerClassName (ex: h-[500px])
        containerClassName,
      )}
    >
      <div className="table-scrollbar w-full flex-1 overflow-auto">
        <table
          data-slot="table"
          className={cn("w-full caption-bottom text-left text-sm", className)}
          {...props}
        />
      </div>
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("border-border [&_tr]:border-b", className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "border-b border-border transition-colors",
        "hover:bg-muted/50",
        "data-[state=selected]:bg-primary/10",
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        // STICKY HEADER FUNCIONAL:
        // Para o sticky funcionar, o overflow deve estar no pai imediato (o que fizemos acima)
        "sticky top-0 z-20",
        // Mesmo cinza do <thead> (bg-muted): mantém o header uniforme e opaco
        // (obrigatório p/ o sticky não deixar o texto vazar por trás).
        "bg-muted",

        "h-11 px-4 text-left align-middle",
        "font-medium text-muted-foreground/90",
        "text-[11px] uppercase tracking-wider",
        "whitespace-nowrap",
        "[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "p-3 align-middle",
        "text-foreground",
        "[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
